/**
 * The first screen: what is going on with me.
 *
 * §14.3 sets the register — plain language at roughly a grade-8 reading level.
 * That constrains wording, not honesty: nothing here softens or omits, and
 * where a number needs a caveat the caveat is shown rather than the number
 * dropped.
 *
 * WHAT THIS SCREEN DOES NOT DO. It does not interpret. There is no "your
 * control is good", no target lines, no trend arrows, no advice. §1.3 forbids
 * this platform diagnosing, and §11.3.1 puts patient-facing interpretation
 * ("should I be worried") behind a clearance this project does not have. The
 * screen shows what is recorded and who recorded it; judging it is the care
 * team's job, and the escalation note says so.
 */

import { type JSX } from 'react';

import { estimatedAverageGlucose } from '@glycemia/glyco-core';
import { quantity } from '@glycemia/units';
import {
  byRecencyDesc,
  codeText,
  doseSummary,
  isActiveCondition,
  isActiveMedication,
  observationValue,
  type FhirObservation,
} from '@glycemia/web-kit';

import { Async } from '../components/Async.js';
import { Value } from '../components/DisplayValue.js';
import { useMyConditions, useMyMedications, useMyObservations } from '../lib/queries.js';

/**
 * The most recent HbA1c, expressed as an average glucose.
 *
 * §2.4 puts every derived metric in `@glycemia/glyco-core` and says nothing
 * else may reimplement one — so this calls `estimatedAverageGlucose` rather
 * than multiplying by 28.7 here. That function returns a `Result` and REFUSES
 * outside 5–12% NGSP, because the ADAG linear fit is not defined there;
 * extrapolating would produce a plausible number that means nothing.
 *
 * When it refuses, the HbA1c is still shown and the average simply is not.
 * Showing a made-up companion number would be worse than showing one number.
 */
const averageGlucoseText = (observation: FhirObservation | undefined): string | null => {
  const value = observation?.valueQuantity?.value;
  const unit = observation?.valueQuantity?.code ?? observation?.valueQuantity?.unit;
  if (value === undefined || (unit !== '%' && unit !== 'mmol/mol')) return null;

  const input = quantity(value, unit);
  if (!input.ok) return null;

  const eag = estimatedAverageGlucose(input.value);
  if (!eag.ok) return null;
  return `about ${Math.round(eag.value.value)} mg/dL average glucose`;
};

export const MyHealth = ({ patientId }: { readonly patientId: string }): JSX.Element => {
  const conditions = useMyConditions(patientId);
  const medications = useMyMedications(patientId);
  const labs = useMyObservations(patientId, 'laboratory');

  // The picker knows no codes (§8.0.1). "The most recent lab result" is a
  // question about recency, not about which LOINC code an HbA1c carries.
  const latestLab = byRecencyDesc(labs.data ?? [])[0];
  const averageGlucose = averageGlucoseText(latestLab);

  return (
    <section aria-labelledby="my-health-heading">
      <h1 id="my-health-heading">My health</h1>

      <section aria-labelledby="conditions-heading">
        <h2 id="conditions-heading">My conditions</h2>
        <Async query={conditions} empty="No conditions are recorded for you.">
          {(items) => (
            <ul className="cards">
              {items.filter(isActiveCondition).map((condition) => (
                <li key={condition.id}>
                  <strong>{codeText(condition.code)}</strong>
                  {condition.onsetDateTime !== undefined && (
                    <span> — since {condition.onsetDateTime}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Async>
      </section>

      <section aria-labelledby="medicines-heading">
        <h2 id="medicines-heading">My medicines</h2>
        <Async query={medications} empty="No medicines are recorded for you.">
          {(items) => (
            <ul className="cards">
              {items.filter(isActiveMedication).map((request) => (
                <li key={request.id}>
                  <strong>{codeText(request.medicationCodeableConcept)}</strong>
                  <span> — {doseSummary(request)}</span>
                </li>
              ))}
            </ul>
          )}
        </Async>
        <p className="notice" role="note">
          This list is what your care team has prescribed. If you have stopped taking something, or
          are taking something that is not here, tell them — it will not update by itself.
        </p>
      </section>

      {latestLab !== undefined && (
        <section aria-labelledby="latest-heading">
          <h2 id="latest-heading">My most recent test</h2>
          <p>
            <strong>{codeText(latestLab.code)}</strong>{' '}
            <Value value={observationValue(latestLab)} />
            {latestLab.effectiveDateTime !== undefined && (
              <span> on {latestLab.effectiveDateTime}</span>
            )}
          </p>
          {averageGlucose !== null && (
            <p>
              {/*
                Stated as an estimate, because it is one: a linear fit from a
                population study, not a measurement of this person.
              */}
              That is {averageGlucose}, estimated from the test above.
            </p>
          )}
        </section>
      )}

      <p className="notice" role="note">
        This page shows what is in your record. It does not give medical advice. If something looks
        wrong, or you feel unwell, contact your care team.
      </p>
    </section>
  );
};
